// useUser.js - atnaujintas failas su rolių patikra
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalStates } from "../GlobalStates";
import { useAlerts } from "./useAlert";
import { supabase } from "../services/supabaseClient";

export const useUser = () => {
    const { setAlert } = useContext(GlobalStates);
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const { userRegisterAlerts: registerAlerts } = useAlerts();

    // Funkcija laukų pavadinimų konvertavimui iš snake_case į camelCase
    const formatToCamelCase = useCallback((data) => {
        const fieldMap = {
            'formos_pavadinimas': 'formosPavadinimas',
            'imones_pavadinimas': 'imonesPavadinimas',
            'form_object': 'formObject',
            'kas_pildo': 'kasPildo',
            'valid_to': 'validTo',
            'created_at': 'createdAt',
            'updated_at': 'updatedAt',
            'uzpildymo_data': 'uzpildymoData',
            'confirmed_email': 'confirmedEmail',
            'confirmed': 'confirmed',
            // Užtikriname, kad vardas ir pavardė išlieka nepakitę, kadangi
            // norime, kad jie išliktų toje pačioje formoje
            'vardas': 'vardas',
            'pavarde': 'pavarde'
        };

        if (Array.isArray(data)) {
            return data.map(item => formatToCamelCase(item));
        }

        if (typeof data === 'object' && data !== null) {
            const result = {};
            for (const key in data) {
                const newKey = fieldMap[key] || key;
                result[newKey] = data[key];
            }
            return result;
        }

        return data;
    }, []);

    // Funkcija laukų pavadinimų konvertavimui iš camelCase į snake_case
    const formatToSnakeCase = useCallback((data) => {
        const fieldMap = {
            'formosPavadinimas': 'formos_pavadinimas',
            'imonesPavadinimas': 'imones_pavadinimas',
            'formObject': 'form_object',
            'kasPildo': 'kas_pildo',
            'validTo': 'valid_to',
            'createdAt': 'created_at',
            'updatedAt': 'updated_at',
            'uzpildymoData': 'uzpildymo_data',
            'adminConfirmed': 'admin_confirmed',
            'confirmed': 'confirmed',
            'resetPasswordToken': 'reset_password_token',
            'confirmationToken': 'confirmation_token',
            'confirmedEmail': 'confirmed_email',
            // Užtikriname, kad vardas ir pavardė išlieka nepakitę, kadangi duomenų bazėje
            // jie jau yra snake_case formatu
            'vardas': 'vardas',
            'pavarde': 'pavarde'
        };

        if (typeof data === 'object' && data !== null) {
            const result = {};
            for (const key in data) {
                const newKey = fieldMap[key] || key;
                result[newKey] = data[key];
            }
            return result;
        }

        return data;
    }, []);

    const getAllUsers = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('users') // Assuming 'users' table
                .select('*');

            if (error) throw error;

            const formattedUsers = formatToCamelCase(data || []);
            setUsers(formattedUsers); 
            return formattedUsers;

        } catch (error) {
            console.error("Error fetching all users:", error.message);
            setAlert(t('Klaida gaunant vartotojų sąrašą'));
            setUsers([]);
            return [];
        }
    }, [formatToCamelCase, setAlert, t]); 

    const getUser = useCallback(async (userId) => {
        if (!userId) return null;
        try {
            const { data, error } = await supabase
                .from('users') // Assuming 'users' table
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                // Handle 'PGRST116' error (No rows found) silently
                if (error.code === 'PGRST116') {
                    return null;
                } else {
                    throw error; // Rethrow other errors
                }
            }
            
            return data ? formatToCamelCase(data) : null;

        } catch (error) {
            console.error("Error fetching user:", error.message);
            setAlert(t('Klaida gaunant vartotojo duomenis'));
            return null;
        }
    }, [formatToCamelCase, setAlert, t]); 

    const updateUser = useCallback(async (userId, info, setter) => {
        try {
            const snakeCaseData = formatToSnakeCase(info);

            const { data, error } = await supabase
                .from('users')
                .update(snakeCaseData) 
                .eq('id', userId)
                .select()
                .single(); // Use select().single() to get the updated record back

            if (error) throw error;

            // --- Admin Role Handling --- 
            // This part is complex and depends on your DB structure. 
            // Placeholder for specific role update logic if needed.
            // Example:
            // if ('adminConfirmed' in info) { // Check if admin status needs update
            //    const newAdminStatus = info.adminConfirmed; 

            // If admin status changed, update the separate admin table or user role
            // This logic needs to be implemented based on how admin roles are stored
            // }

            setAlert(t('Duomenys sėkmingai atnaujinti!'));
            return data ? formatToCamelCase(data) : true; // Return updated user or true
        } catch (error) {
            console.error("Error updating user:", error.message);
            setAlert(t('Klaida atnaujinant vartotojo duomenis'));
            return false;
        }
    }, [formatToCamelCase, formatToSnakeCase, setAlert, t]); 

    const DELETE = useCallback(async (userId) => {
        if (!userId) return false;
        try {
            // IMPORTANT: Decide delete strategy
            // Option 1: Delete only the profile (user can still log in if auth entry exists)
            const { error } = await supabase
                .from('users')
                .delete()
                .eq('id', userId);
            
            // Option 2: Delete the auth user (REQUIRES ADMIN PRIVILEGES for the Supabase client)
            // const { error } = await supabase.auth.admin.deleteUser(userId);

            if (error) throw error;
            setAlert(t('Vartotojas sėkmingai ištrintas'));
            return true;
        } catch (error) {
            console.error("Error deleting user:", error.message);
            setAlert(t('Klaida trinant vartotoją'));
            return false;
        }
    }, [setAlert, t]);

    const create = useCallback(async (info = {}, setter = (s) => s) => {
        try {
            // Konvertuojame duomenis į snake_case
            const formattedInfo = formatToSnakeCase(info);
            
            // IMPORTANT: Decide create strategy
            // Option 1: Create only a profile entry (requires user to exist in auth.users)
            // Assumes 'id' is provided in 'info' and matches an existing auth.users.id
            // formattedInfo.created_at = new Date(); // Handled by DB default?
            // formattedInfo.updated_at = new Date(); // Handled by DB default?
            
            // const { data, error } = await supabase
            //     .from('users')
            //     .insert(formattedInfo)
            //     .select()
            //     .single();

            // Option 2: Create Auth User AND Profile (Requires ADMIN privileges, email/password in 'info')
            // This is more complex as it involves multiple steps and error handling.
            // Example creating auth user (needs email, password):
             if (!info.email || !info.password) {
                 throw new Error('Email and password are required to create a new user.');
             }
             const { data: authData, error: authError } = await supabase.auth.admin.createUser({
                 email: info.email,
                 password: info.password,
                 email_confirm: true, // Or false if you want email verification
                 // user_metadata can be used for profile data, but often profile table is better
                 // user_metadata: { vardas: info.vardas, pavarde: info.pavarde, ... }
             });

             if (authError) throw authError;

             // Now create the profile entry using the new user's ID
             const userId = authData.user.id;
             formattedInfo.id = userId; // Ensure the profile is linked to the auth user
             // Remove password before inserting into users
             delete formattedInfo.password; 
             delete formattedInfo.email; // Email is in auth.users
             
             const { data, error } = await supabase
                 .from('users')
                 .insert(formattedInfo) 
                 .select()
                 .single();
            // --- End Option 2 ---
            
            if (error) {
                // If profile creation failed after auth user creation, maybe delete the auth user?
                // await supabase.auth.admin.deleteUser(userId); // Rollback?
                let errorsM = [];
                if (error.code === '23505') { // Uniqueness violation (e.g., duplicate email if profile has email)
                    errorsM.push('Vartotojas su tokiu el. paštu jau egzistuoja');
                    // Consider deleting the auth user if profile insert failed due to unique constraint
                    await supabase.auth.admin.deleteUser(userId);
                } else {
                    errorsM.push(error.message);
                }
                registerAlerts(errorsM);
                setter(false);
                return null;
            }
            
            // Placeholder for specific role creation logic if needed.
            // Example:
            // if ('adminConfirmed' in info) {
            //    const isAdmin = info.adminConfirmed;

            // If the new user is an admin, set their role accordingly
            // This logic needs to be implemented based on role storage
            // }
            
            setter(true);
            setAlert(t('Vartotojas sukurtas sėkmingai!'));
            return data ? formatToCamelCase(data) : null;
        } catch (error) {
            console.error("Error creating user:", error.message);
            // Handle specific Supabase auth errors if using admin.createUser
            if (error.message.includes('User already registered')) {
                 registerAlerts(['Vartotojas su tokiu el. paštu jau egzistuoja']);
            } else {
                setAlert(t('Klaida kuriant vartotoją') + `: ${error.message}`);
            }
            setter(false);
            return null;
        }
    }, [formatToCamelCase, formatToSnakeCase, registerAlerts, setAlert, t]);

    const confirmUser = useCallback(async (userId) => {
        if (!userId) return false;
        try {
            // Pirmiausia gauname vartotojo informaciją - NEREIKIA, tiesiog atnaujinam
            // const user = await getUser(userId); 
            // if (!user) {
            //     setAlert(t('Klaida: vartotojas nerastas'));
            //     return false;
            // }
            
            // Atnaujinami vartotojo duomenys - tik confirmed statusas
            // const updatedUser = {
            //     ...user,
            //     confirmed: true
            // };
            // Išsaugoma
            // const result = await updateUser(userId, updatedUser); // Nereikia kviesti viso updateUser

             const { error } = await supabase
                .from('users')
                .update({ confirmed: true, confirmed_at: new Date() }) // Assuming 'confirmed' column exists
                .eq('id', userId);

            if (error) throw error;

            setAlert(t('Įmonė sėkmingai patvirtinta!'));
            return true;
            // if (result) {
            //     setAlert(t('Įmonė sėkmingai patvirtinta!'));
            //     return true;
            // } else {
            //     setAlert(t('Klaida patvirtinant įmonę. Bandykite dar kartą.'));
            //     return false;
            // }
        } catch (error) {
            console.error("Error confirming user:", error.message);
            setAlert(t('Klaida patvirtinant įmonę. Bandykite dar kartą.'));
            return false;
        }
    }, [setAlert, t]); 

    return {
        users,
        getUser,
        getAllUsers,
        DELETE,
        updateUser,
        create,
        confirmUser
    };
};